import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Pagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Button,
    TextField,
    MenuItem,
    Grid,
    Alert,
} from "@mui/material";
import { Refresh, CallOutlined, ChatOutlined, Handshake, TaskAlt, Close, EditOutlined, EventRepeat } from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";
import CRMBadge from "../common/CRMBadge";
import Cookies from "js-cookie";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import {
    TASK_DATE_PRESETS,
    buildTaskDateQueryParams,
    getTaskDateRangeStatusMessage,
    getTaskDateFilterEmptyMessage,
    getTaskDueDateRangeForMonths,
    isTaskDateFilterComplete,
    isValidTaskDateRange,
    matchTaskDatePresetMonths,
} from "../common/crmTaskDateRange";

const FILTER_OPTIONS = [
    { label: "Pending",   value: "pending",   isStatus: true },
    { label: "Overdue",   value: "overdue",   isStatus: true },
    { label: "Completed", value: "completed", isStatus: true },
    { label: "All",       value: "" },
    { label: "Calls",     value: "call" },
    { label: "Messages",  value: "message" },
    { label: "Meetings",  value: "meeting" },
    { label: "Follow-ups", value: "follow-up" },
];

const TYPE_COLORS = {
    call:         { bg: "#d1fae5", text: "#065f46" },
    message:      { bg: "#dbeafe", text: "#1d4ed8" },
    meeting:      { bg: "#ede9fe", text: "#5b21b6" },
    "follow-up":  { bg: "#ffedd5", text: "#c2410c" },
};

const STATUS_COLORS = {
    pending:   { bg: "#fef9c3", text: "#854d0e" },
    overdue:   { bg: "#fee2e2", text: "#b91c1c" },
    completed: { bg: "#d1fae5", text: "#065f46" },
};

const TYPE_LABELS = {
    call:         "Call",
    message:      "Message",
    meeting:      "Meeting",
    "follow-up":  "Follow-up",
};

const STATUS_LABELS = {
    pending:   "Pending",
    overdue:   "Overdue",
    completed: "Completed",
};

const typeIcon = (type) => {
    switch (type) {
        case "call":         return <CallOutlined sx={{ fontSize: 16 }} />;
        case "message":      return <ChatOutlined sx={{ fontSize: 16 }} />;
        case "meeting":      return <Handshake sx={{ fontSize: 16 }} />;
        case "follow-up":    return <EventRepeat sx={{ fontSize: 16 }} />;
        default:             return <TaskAlt sx={{ fontSize: 16 }} />;
    }
};

const fmtDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const getCurrentUserId = () => {
    const raw = Cookies.get("userId");
    if (raw == null || raw === "") return "";
    try {
        const parsed = JSON.parse(raw);
        return parsed != null ? String(parsed) : "";
    } catch {
        return String(raw);
    }
};

const getTaskAssignUserId = (t) => {
    if (!t) return "";
    if (t.assign_user_id != null) return String(t.assign_user_id);
    if (t.assigned_user_id != null) return String(t.assigned_user_id);
    if (t.assigned_user?.id != null) return String(t.assigned_user.id);
    if (typeof t.assign_user === "number") return String(t.assign_user);
    if (typeof t.assign_user === "string" && /^\d+$/.test(t.assign_user)) return t.assign_user;
    return "";
};

/** Assignees: PATCH /crm/tasks/status/{id}. Owners/admins: PUT /crm/tasks/{id}. */
const TaskStatusEditDialog = ({ open, task, onClose, onSaved }) => {
    const notify = useNotification();
    const [status, setStatus] = useState("pending");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !task) return;
        setStatus(task.status || "pending");
        setDescription(task.description ?? "");
    }, [open, task]);

    const handleSubmit = async () => {
        if (!task?.id) return;
        setSaving(true);
        try {
            const payload = {
                status,
                description: description.trim(),
            };
            const currentUserId = getCurrentUserId();
            const assignUserId = getTaskAssignUserId(task);
            const isAssignee =
                Boolean(currentUserId) && currentUserId === assignUserId;

            if (isAssignee) {
                await axiosInstance.patch(
                    `${base_url}/crm/tasks/status/${task.id}`,
                    payload
                );
            } else {
                await axiosInstance.put(
                    `${base_url}/crm/tasks/${task.id}`,
                    payload
                );
            }
            notify("Task updated", "success");
            onSaved?.();
            onClose();
        } catch (err) {
            notify(err?.response?.data?.message || "Could not update task", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !saving && onClose()}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 700,
                    fontSize: "1rem",
                    pb: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "rgba(17,24,39,0.06)",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                        }}
                    >
                        <TaskAlt sx={{ fontSize: 18, color: "text.primary" }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }} noWrap title={task?.title}>
                            Update task
                        </Typography>
                        {task?.title ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }} noWrap title={task.title}>
                                {task.title}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Close">
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="overdue">Overdue</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Description"
                            placeholder="e.g. Called client; sent quote."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button size="small" onClick={onClose} disabled={saving} sx={WB_SX.secondaryButton}>
                    Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleSubmit} disabled={saving} sx={WB_SX.primaryButton}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const CRMActivity = () => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [tasks, setTasks]       = useState([]);
    const [total, setTotal]       = useState(0);
    const [page, setPage]         = useState(1);
    const [perPage]               = useState(20);
    const [typeFilter, setTypeFilter]     = useState("");
    const [statusFilter, setStatusFilter] = useState("pending");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo]     = useState("");
    const [loading, setLoading]   = useState(false);
    const [usersMap, setUsersMap] = useState({}); // { id -> name }
    const [editTask, setEditTask] = useState(null);

    // Fetch allowed users once to resolve assign_user IDs to names
    useEffect(() => {
        axiosInstance.get(`${base_url}/whatsapp-business/users/allowed`)
            .then((res) => {
                const list = Array.isArray(res.data?.data) ? res.data.data : [];
                const map  = {};
                list.forEach((u) => { map[String(u.id)] = u.name; });
                setUsersMap(map);
            })
            .catch(() => {});
    }, []);

    const fetchTasks = useCallback(async (pg = 1, tf = typeFilter, sf = statusFilter, from = dateFrom, to = dateTo) => {
        if (!isTaskDateFilterComplete(from, to)) return;
        const dateParams = buildTaskDateQueryParams(from, to);
        if (dateParams === null) {
            notifyRef.current("To date must be on or after From date", "error");
            return;
        }
        setLoading(true);
        try {
            const params = {
                page: pg,
                per_page: perPage,
                ...dateParams,
            };
            if (sf) params.status = sf;
            if (tf) params.type   = tf;

            const res       = await axiosInstance.get(`${base_url}/crm/tasks`, { params });
            // Response: { success, data: { data: [...tasks], total, current_page, ... } }
            const paginator = res.data?.data ?? {};
            const list      = Array.isArray(paginator.data) ? paginator.data : [];
            setTasks(list);
            setTotal(paginator.total ?? list.length);
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to load activity", "error");
        } finally {
            setLoading(false);
        }
    }, [typeFilter, statusFilter, dateFrom, dateTo, perPage]);

    useEffect(() => {
        if (!isTaskDateFilterComplete(dateFrom, dateTo)) return;
        if (dateFrom && dateTo && !isValidTaskDateRange(dateFrom, dateTo)) return;
        fetchTasks(page, typeFilter, statusFilter, dateFrom, dateTo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, typeFilter, statusFilter, dateFrom, dateTo]);

    const handleFilterClick = (opt) => {
        setPage(1);
        if (opt.isStatus) {
            setStatusFilter((prev) => (prev === opt.value ? "" : opt.value));
            setTypeFilter("");
        } else {
            setTypeFilter((prev) => (prev === opt.value ? "" : opt.value));
            setStatusFilter("");
        }
    };

    const handleDateFromChange = (value) => {
        setPage(1);
        setDateFrom(value);
    };

    const handleDateToChange = (value) => {
        setPage(1);
        setDateTo(value);
    };

    const handlePresetMonths = (months) => {
        const range = getTaskDueDateRangeForMonths(months);
        setPage(1);
        setDateFrom(range.from);
        setDateTo(range.to);
    };

    const handleClearDateFilter = () => {
        setPage(1);
        setDateFrom("");
        setDateTo("");
    };

    const activeFilter = typeFilter || statusFilter || "";
    const totalPages   = Math.ceil(total / perPage);
    const dateRangeInvalid = Boolean(
        (dateFrom && !dateTo) || (!dateFrom && dateTo) || (dateFrom && dateTo && !isValidTaskDateRange(dateFrom, dateTo))
    );
    const activePresetMonths = matchTaskDatePresetMonths(dateFrom, dateTo);
    const taskDateRangeNote = getTaskDateRangeStatusMessage(dateFrom, dateTo);
    const taskListEmptyMessage = activeFilter
        ? `No tasks match the selected filter${dateFrom && dateTo ? " and date range" : ""}.`
        : getTaskDateFilterEmptyMessage(dateFrom, dateTo);

    return (
        <Box sx={WB_SX.page}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={WB_SX.pageTitle}>
                        Tasks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total} task{total !== 1 ? "s" : ""} across all contacts &amp; deals
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton
                        size="small"
                        onClick={() => fetchTasks(page, typeFilter, statusFilter, dateFrom, dateTo)}
                        sx={WB_SX.secondaryButton}
                    >
                        <Refresh fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Date range filter */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 1.5 }}>
                <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={dateFrom}
                    onChange={(e) => handleDateFromChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={dateRangeInvalid}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, minWidth: 160 }}
                />
                <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={dateTo}
                    onChange={(e) => handleDateToChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={dateRangeInvalid}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, minWidth: 160 }}
                />
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                    {TASK_DATE_PRESETS.map((preset) => {
                        const isActive = activePresetMonths === preset.months;
                        return (
                            <Chip
                                key={preset.label}
                                label={preset.label}
                                size="small"
                                variant={isActive ? "filled" : "outlined"}
                                onClick={() => handlePresetMonths(preset.months)}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    borderRadius: 999,
                                    minWidth: 40,
                                    cursor: "pointer",
                                    ...(isActive && {
                                        backgroundColor: "#111827",
                                        color: "#fff",
                                        "&:hover": { backgroundColor: "#1f2937" },
                                    }),
                                }}
                            />
                        );
                    })}
                    <Chip
                        label="Clear"
                        size="small"
                        variant={!dateFrom && !dateTo ? "filled" : "outlined"}
                        onClick={handleClearDateFilter}
                        sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            borderRadius: 999,
                            cursor: "pointer",
                            ...(!dateFrom && !dateTo && {
                                backgroundColor: "#111827",
                                color: "#fff",
                                "&:hover": { backgroundColor: "#1f2937" },
                            }),
                        }}
                    />
                </Box>
            </Box>
            <Alert severity="info" sx={{ mb: 2, py: 0.5, "& .MuiAlert-message": { fontSize: "0.8rem" } }}>
                {taskDateRangeNote}
            </Alert>

            {/* Filter chips */}
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 3 }}>
                {FILTER_OPTIONS.map((opt) => {
                    const isActive = opt.value === "" ? activeFilter === "" : (opt.isStatus ? statusFilter === opt.value : typeFilter === opt.value);
                    return (
                        <Chip
                            key={opt.label}
                            label={opt.label}
                            size="small"
                            variant={isActive ? "filled" : "outlined"}
                            onClick={() => handleFilterClick(opt)}
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.72rem",
                                borderRadius: 999,
                                cursor: "pointer",
                                ...(isActive && {
                                    backgroundColor: "#111827",
                                    color: "#fff",
                                    "&:hover": { backgroundColor: "#1f2937" },
                                }),
                            }}
                        />
                    );
                })}
            </Box>

            {/* Task list */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : tasks.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: "center" }}>
                    {taskListEmptyMessage}
                </Typography>
            ) : (
                <>
                    <Box>
                        {tasks.map((task, i) => {
                            const tc = TYPE_COLORS[task.type]   || { bg: "#f3f4f6", text: "#374151" };
                            const sc = STATUS_COLORS[task.status] || { bg: "#f3f4f6", text: "#374151" };

                            return (
                                <Box
                                    key={task.id}
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        py: 2,
                                        borderBottom: i < tasks.length - 1 ? `1px solid ${WB_UI.color.border}` : "none",
                                    }}
                                >
                                    {/* Icon bubble */}
                                    <Box
                                        sx={{
                                            width: 36, height: 36, borderRadius: "50%",
                                            background: tc.bg,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            color: tc.text, flexShrink: 0,
                                        }}
                                    >
                                        {typeIcon(task.type)}
                                    </Box>

                                    {/* Content */}
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                                                {task.title}
                                            </Typography>
                                            <CRMBadge label={TYPE_LABELS[task.type] || "Task"} bg={tc.bg} text={tc.text} />
                                            <CRMBadge label={STATUS_LABELS[task.status] || "Pending"} bg={sc.bg} text={sc.text} />
                                        </Box>

                                        {task.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.5 }}>
                                                {task.description}
                                            </Typography>
                                        )}

                                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                            {task.deal?.title && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Deal: {task.deal.title}
                                                </Typography>
                                            )}
                                            {(task.assign_user || task.assign_user_name || task.assigned_user?.name) && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Assigned: {task.assigned_user?.name || task.assign_user_name || usersMap[String(task.assign_user)] || task.assign_user}
                                                </Typography>
                                            )}
                                            {task.due_date && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: task.status === "overdue" ? "error.main" : "text.secondary" }}
                                                >
                                                    Due: {fmtDate(task.due_date)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Tooltip title="Edit status & description">
                                        <IconButton
                                            size="small"
                                            onClick={() => setEditTask(task)}
                                            sx={{ alignSelf: "flex-start", color: "text.secondary", "&:hover": { color: "primary.main" } }}
                                        >
                                            <EditOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>

                    {totalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, v) => setPage(v)}
                                size="small"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </>
            )}

            <TaskStatusEditDialog
                open={Boolean(editTask)}
                task={editTask}
                onClose={() => setEditTask(null)}
                onSaved={() => fetchTasks(page, typeFilter, statusFilter, dateFrom, dateTo)}
            />
        </Box>
    );
};

export default CRMActivity;
